import React, { useMemo } from "react";
import {
  Table,
  ButtonToolbar,
  Button,
  Badge,
  Input,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage, useIntl } from "react-intl";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { compareByName } from "../../Utilities/sortHelper";

const StudentTable = (props) => {
  const intl = useIntl();

  // Kopieras före sortering: sort() muterar på plats, och ClassOverview skickar in
  // React Query-cachens egen array. Filtret låg dessutom efter sorteringen och läste
  // om props.students, så den filtrerade listan var sorterad bara tack vare muteringen.
  const students = useMemo(() => {
    const filter = props.filter?.toLowerCase();
    const matching = filter
      ? props.students.filter(
          (t) =>
            t.name.toLowerCase().includes(filter) ||
            t.email.toLowerCase().includes(filter),
        )
      : props.students;

    return [...matching].sort(compareByName);
  }, [props.students, props.filter]);

  var mappedStudents = students.map((student, id) => {
    const studentLicenses = props.licensesFor ? props.licensesFor(student) : [];

    // Ej aktiverat konto dämpar radens text; ingen kolumn får plats i
    // klassöversikten. Knappar och badges har egna färger och påverkas inte.
    // Tooltipen förklarar för den som inte känner till mönstret, och den dolda
    // texten i namncellen gör statusen läsbar för skärmläsare.
    const notActivated = !student.activatedAccount;

    return (
      <tr
        key={id}
        style={{ verticalAlign: "middle" }}
        className={notActivated ? "row-not-activated" : undefined}
        title={
          notActivated ? intl.formatMessage({ id: "NotActivated" }) : undefined
        }
      >
        <td>{student.id}</td>
        <td>
          <Button
            className="ps-0 text-start name-link"
            color="link"
            onClick={() => props.toggleEdit(student)}
            title={intl.formatMessage(
              { id: "EditInformationFor" },
              { name: student.name },
            )}
          >
            {student.name}
          </Button>
          {notActivated && (
            <span className="visually-hidden">
              <FormattedMessage id="NotActivated" />
            </span>
          )}
        </td>
        <td>{student.email}</td>
        {props.showClass && <td>{student.class.name}</td>}

        <td>
          {studentLicenses.map((license, i) => (
            <div key={i}>
              <Badge pill color={license.color}>
                {license.labelId ? (
                  <FormattedMessage id={license.labelId} />
                ) : (
                  license.name
                )}
              </Badge>
            </div>
          ))}
        </td>

        {props.onToggleStudent && (
          <td className="text-center">
            {/* Ingen bock för den som redan har licensen — den gröna badgen säger det. */}
            {props.licenseNeededFor(student) && (
              <Input
                type="checkbox"
                className="mt-0 align-middle"
                checked={props.selectedStudentIds.includes(student.id)}
                onChange={() => props.onToggleStudent(student.id)}
              />
            )}
          </td>
        )}

        <td>
          <ButtonToolbar className="float-end">
            <div className="d-flex gap-1">
              <ButtonSendActivationMail className="float-end" user={student} />
              {props.onBuyLicense &&
                (() => {
                  const canBuy = props.canBuyLicenseFor
                    ? props.canBuyLicenseFor(student)
                    : true;
                  const title = intl.formatMessage(
                    { id: canBuy ? "BuyLicenseFor" : "AlreadyHasLicense" },
                    { name: student.name },
                  );

                  return (
                    <span title={title} className="btn-group">
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        disabled={!canBuy}
                        onClick={() => props.onBuyLicense(student)}
                      >
                        <FontAwesomeIcon icon="shopping-cart" />
                      </Button>
                    </span>
                  );
                })()}
              {props.onDeleteStudent && (
                <Button
                  title={intl.formatMessage({ id: "DeleteStudent" })}
                  outline
                  color="danger"
                  size="sm"
                  onClick={() => props.onDeleteStudent(student)}
                >
                  <FontAwesomeIcon icon="trash" />
                </Button>
              )}
            </div>
          </ButtonToolbar>
        </td>
      </tr>
    );
  });

  return (
    <Table hover responsive striped className="school-table">
      <thead>
        <tr>
          <th>Id</th>
          <th><FormattedMessage id="Name" /></th>
          <th><FormattedMessage id="Email" /></th>
          {props.showClass && <th><FormattedMessage id="Class" /></th>}
          <th><FormattedMessage id="Licenses" /></th>
          {props.onToggleStudent && (
            <th className="text-center">{props.selectionHeader}</th>
          )}
          <th />
        </tr>
      </thead>
      <tbody>{mappedStudents}</tbody>
    </Table>
  );
};

export default StudentTable;
